import axios from "axios";
import { envVariables, notification } from "../Common/Store/CommonSignals";
import { entryValues, template } from "../Entry/Manage/EditEntrySignals";
import math from "../Mathjs/CustomMathjsInstance";

export const handleAutomations = async ({
  trigger,
  automation,
  entryData,
  manualEvent,
  handleSubmit,
}) => {
  if (!template.value.properties.DisableTemplateAutomations) {
    const Automations = template.value?.properties?.Automations?.map((automation) => {
      return { ...automation.model, name: automation.name };
    });
    const filteredAutomations = Automations?.filter((automation) => {
      const triggerNode = getTriggerNode(automation);
      return triggerNode?.extras?.properties?.TriggerType === trigger;
    });

    let automationsToProcess = [];
    if (automation) {
      const automationToProcess = Automations?.find((_automation) => {
        return _automation.name === automation;
      });
      if (automationToProcess) {
        automationsToProcess.push(automationToProcess);
      }
    } else {
      automationsToProcess = filteredAutomations;
    }
    automationsToProcess?.forEach((automation) => {
      try {
        traverseAutomation(
          JSON.tryParse(JSON.stringify(automation), "{}"),
          entryData,
          manualEvent,
          handleSubmit
        );
      } catch (error) {
        console.error("Error traversing automation:", error);
      }
    });
  } else {
    notification.value = {
      show: true,
      type: "warning",
      message: `Automations are disabled for this template`,
    };
  }
};

function getTriggerNode(automation) {
  const nodes = automation.layers.find((layer) => {
    return layer.type === "diagram-nodes";
  }).models;

  for (let key in nodes) {
    if (key.includes("TriggerNode")) {
      return nodes[key];
    }
  }
  return null;
}

async function traverseAutomation(automation, entryData, manualEvent, handleSubmit) {
  const nodes = automation.layers.find((layer) => {
    return layer.type === "diagram-nodes";
  }).models;
  const links = automation.layers.find((layer) => {
    return layer.type === "diagram-links";
  }).models;

  processVariableNodes(
    Object.values(nodes).filter((node) => node.extras.properties.Type === "Variable"),
    Object.values(nodes),
    Object.values(links)
  );

  // Process function nodes
  const functionNodes = Object.values(nodes).filter(
    (node) => node.extras.properties.Type === "Function"
  );
  await processFunctionNodes(
    functionNodes,
    Object.values(nodes),
    Object.values(links),
    automation.name
  );

  // Process condition nodes
  processConditionNodes(
    Object.values(nodes).filter((node) => node.extras.properties.Type === "Condition")
  );

  evaluateAutomation(
    Object.values(nodes),
    Object.values(links),
    entryData,
    manualEvent,
    handleSubmit
  );
}

function processVariableNodes(variableNodes, nodes, links) {
  function getVariableValue(node) {
    let variableValue = node.extras.properties.Value;
    const fieldPattern = /\{\{(.+?)\}\}/g;
    const mathPattern = /=\[\[(.+?)\]\]/g;
    variableValue = variableValue.replace(fieldPattern, (match, fieldName) => {
      if (entryValues.value && Object.hasOwn(entryValues.value, fieldName)) {
        const fieldValue =
          typeof entryValues.value[fieldName] === "object"
            ? JSON.stringify(entryValues.value[fieldName])
            : entryValues.value[fieldName];

        // Convert to JSON string to preserve structure
        return fieldValue;
      } else {
        console.warn(`Field "${fieldName}" not found in entryValues.value`);
        return match;
      }
    });
    variableValue = variableValue.replace(mathPattern, (match, expression) => {
      try {
        const result = math.evaluate(expression);
        return result.toString();
      } catch (error) {
        console.error(`Error evaluating expression: ${match}`, error);
        return match;
      }
    });

    return variableValue;
  }

  variableNodes.forEach((node) => {
    const outPorts = node.portsOutOrder;

    outPorts.forEach((outPort) => {
      const linksFromNode = links.filter((link) => {
        return link.sourcePort === outPort;
      });

      linksFromNode.forEach((link) => {
        let targetNode = nodes.find((node) => {
          return node.id === link.target;
        });
        const targetPort = targetNode.ports.find((port) => {
          return port.id === link.targetPort;
        });
        if (!targetNode.extras.properties.Scope) {
          targetNode.extras.properties.Scope = {};
        }

        let variableValue = getVariableValue(node);
        if (targetPort.name !== "Body") {
          variableValue = stripHtmlTags(variableValue);
        }

        targetNode.extras.properties = {
          ...targetNode.extras.properties,
          Scope: {
            ...targetNode.extras.properties.Scope,
            [targetPort.name]: variableValue,
          },
        };
      });
    });
  });
}

async function processFunctionNodes(functionNodes, nodes, links, automationName) {
  const processedNodes = new Set();
  const parser = math.parser();

  // Define custom functions
  parser.set("getProperty", (obj, prop) => {
    return obj[prop];
  });

  parser.set("map", (arr, func) => {
    return arr.map((item) => parser.evaluate(func, { x: item }));
  });

  async function getFunctionValue(node, automationName) {
    const functionType = node.extras.properties.Function;
    const relevantPorts = node.portsInOrder;
    const scope = {};

    console.log("Relevant ports:", relevantPorts);
    console.log("Node:", node);
    relevantPorts.forEach((portId, index) => {
      const port = node.ports.find((p) => p.id === portId);
      if (port) {
        scope[`arg${index}`] = node.extras.properties.Scope[port.name];
      } else {
        console.warn(`Port with ID ${portId} not found for node ${node.id}`);
      }
    });

    console.log("Scope:", scope);

    try {
      switch (functionType) {
        case "parse":
          if (typeof scope.arg0 !== "string") {
            console.error(`Error in parse function: Expected string, got ${typeof scope.arg0}`);
            return null;
          }
          parser.set("parse", (str) => JSON.tryParse(str));
          return parser.evaluate(`parse(${scope.arg0})`);
        case "map":
          if (!Array.isArray(scope.arg0) || typeof scope.arg1 !== "string") {
            console.error(`Error in map function: Invalid arguments`);
            return null;
          }
          return scope.arg0.map((item) => {
            if (typeof item === "object" && item !== null) {
              return item[scope.arg1];
            } else {
              console.warn(`Cannot get property ${scope.arg1} of non-object item`);
              return undefined;
            }
          });
        case "getProperty": {
          //all three other functions combined into one... parse if needed, the map to get the property, then join into a string
          if (typeof scope.arg0 !== "string" || typeof scope.arg1 !== "string") {
            console.error(`Error in getProperty function: Invalid arguments`);
            return null;
          }
          parser.set("parse", (str) => JSON.tryParse(str));
          //parse if arg0 is a string
          let parsed = scope.arg0;
          if (typeof scope.arg0 === "string") {
            parsed = parser.evaluate(`parse(${scope.arg0})`);
          }
          //map to get the property
          let mapped = parsed;
          if (Array.isArray(parsed)) {
            mapped = parsed.map((item) => {
              if (typeof item === "object" && item !== null) {
                return item[scope.arg1];
              } else {
                console.warn(`Cannot get property ${scope.arg1} of non-object item`);
                return undefined;
              }
            });
          }
          //join into a string
          return mapped.join(", ");
        }
        case "join":
          if (!Array.isArray(scope.arg0)) {
            console.error(`Error in join function: Expected array, got ${typeof scope.arg0}`);
            return null;
          }
          return scope.arg0.join(", ");
        case "getEmailsFromPeoplePickerInTable": {
          return getEmailsFromPeoplePickerInTable(scope, automationName);
        }
        case "getEmailsFromPeoplePicker":
          return getEmailsFromPeoplePicker(scope);
        default: {
          // For other functions, directly evaluate using mathjs
          const args = Object.keys(scope).join(", ");
          const expression = `${functionType}(${args})`;
          return parser.evaluate(expression, scope);
        }
      }
    } catch (error) {
      console.error(`Error evaluating function ${functionType}:`, error);
      return null;
    }
  }

  async function processNode(node) {
    if (processedNodes.has(node.id)) {
      console.log(`Node ${node.id} already processed. Skipping.`);
      return;
    }

    console.log(`Processing node: ${node.id}`);
    console.log("Node properties:", node.extras.properties);

    const allInputsReady = await ensureInputsReady(node);

    if (!allInputsReady) {
      console.log(`Node ${node.id} is not ready for processing. Retrying...`);
      return await processNode(node);
    }

    await evaluateAndPropagate(node);
    processedNodes.add(node.id);
    console.log(`Finished processing node ${node.id}`);
  }

  async function ensureInputsReady(node) {
    const relevantPorts = node.portsInOrder;
    let allReady = true;

    for (const portId of relevantPorts) {
      const port = node.ports.find((p) => p.id === portId);
      if (!port) {
        console.warn(`Port with ID ${portId} not found for node ${node.id}`);
        continue;
      }

      console.log(`Checking port ${port.name} for node ${node.id}`);

      if (isInputReady(node, port)) {
        console.log(`Input ready for port ${port.name} of node ${node.id}`);
      } else {
        console.log(
          `Input not ready for port ${port.name} of node ${node.id}. Processing dependencies...`
        );
        allReady = false;
        await processDependencies(node, portId);
      }
    }

    return allReady;
  }

  function isInputReady(node, port) {
    return node.extras.properties.Scope?.[port.name] !== undefined;
  }

  async function processDependencies(node, portId) {
    const incomingLinks = links.filter(
      (link) => link.targetPort === portId && link.target === node.id
    );

    for (const link of incomingLinks) {
      const sourceNode = nodes.find((n) => n.id === link.source);
      if (sourceNode.extras.properties.Type === "Function") {
        console.log(`Processing dependency node ${sourceNode.id} for node ${node.id}`);
        await processNode(sourceNode);
      }
    }
  }

  async function evaluateAndPropagate(node) {
    console.log(`All inputs ready for node ${node.id}. Evaluating function...`);
    const functionValue = await getFunctionValue(node, automationName);
    console.log(`Function value for node ${node.id}:`, functionValue);

    if (functionValue === null) {
      console.warn(`Function node ${node.id} returned null. Skipping propagation.`);
      return;
    }

    for (const outPort of node.portsOutOrder) {
      propagateValue(node, outPort, functionValue);
    }
  }

  function propagateValue(node, outPort, functionValue) {
    const outgoingLinks = links.filter(
      (link) => link.sourcePort === outPort && link.source === node.id
    );

    for (const link of outgoingLinks) {
      const targetNode = nodes.find((n) => n.id === link.target);
      const targetPort = targetNode.ports.find((port) => port.id === link.targetPort);

      if (!targetNode.extras.properties.Scope) {
        targetNode.extras.properties.Scope = {};
      }
      targetNode.extras.properties.Scope[targetPort.name] = functionValue;
      console.log(`Propagated value to port ${targetPort.name} of node ${targetNode.id}`);
    }
  }

  for (const node of functionNodes) {
    await processNode(node);
  }
}

function processConditionNodes(conditionNodes) {
  conditionNodes.forEach((node) => {
    const expression = node.extras.properties.Expression || "";
    const scopeValue = node.extras.properties.Scope?.Value;

    if (!expression && scopeValue === undefined) {
      node.extras.properties.ConditionResult = true;
      return;
    }

    const context = {
      ...(entryValues.value || {}),
      ...(scopeValue !== undefined ? { Value: scopeValue } : {}),
    };

    try {
      const resolvedExpression = evaluateExpressionTemplate(expression, context);

      if (resolvedExpression === "null") {
        node.extras.properties.ConditionResult = null;
        return;
      }

      node.extras.properties.ConditionResult = toBooleanResult(resolvedExpression);
    } catch (error) {
      console.error(`Error evaluating condition expression: "${expression}"`, error);
      node.extras.properties.ConditionResult = true;
    }
  });
}

function evaluateAutomation(nodes, links, entryData, manualEvent, handleSubmit) {
  async function processNode(node, nodes, links) {
    const nodeType = node.extras.properties.Type;

    if (!node.portsOutOrder.length > 0) {
      return;
    }

    if (nodeType === "Condition") {
      const conditionResult = node.extras.properties.ConditionResult;

      if (conditionResult === null || conditionResult === "null") {
        console.log(
          `Condition "${node.extras.properties.Name}" evaluated to null. Stopping automation here.`
        );
        return;
      }

      const conditionPort = conditionResult ? "onConditionTrue" : "onConditionFalse";
      console.log(
        `Condition "${node.extras.properties.Name}" evaluated to ${conditionResult}. Following ${conditionPort}.`
      );
      const link = links.find((link) => {
        const sourcePort = node.ports.find((p) => p.id === link.sourcePort);
        return sourcePort?.name === conditionPort;
      });
      if (link) {
        const targetNode = nodes.find((node) => {
          return node.id === link.target;
        });
        processNode(targetNode, nodes, links);
      }
      return;
    }

    if (nodeType === "Event") {
      const response = await triggerEvent(node, entryData, manualEvent, handleSubmit);
      console.log(response);
    }

    const outPorts = node.portsOutOrder;
    let linksFromNode = [];
    outPorts.forEach((outPort) => {
      linksFromNode.push(
        links.find((link) => {
          return link.sourcePort === outPort;
        })
      );
    });
    linksFromNode.forEach((link) => {
      const targetNode = nodes.find((node) => {
        return node.id === link.target;
      });
      processNode(targetNode, nodes, links);
    });
  }

  const triggerNode = nodes.find((node) => {
    return node.extras.properties.Type === "Trigger";
  });
  processNode(triggerNode, nodes, links);
}

async function triggerEvent(node, entryData, manualEvent, handleSubmit) {
  const eventType = node.extras.properties.Event;
  let response;
  switch (eventType) {
    case "Send Email":
      response = await sendEmail(node);
      break;
    case "Send Approval":
      response = await sendApproval(node, entryData);
      break;
    case "Open Hyperlink":
      response = openHyperlink(node);
      break;
    case "Submit Form":
      response = submitTemplate(manualEvent, handleSubmit);
      break;
    default:
      break;
  }

  if (eventType === "Send Email" || eventType === "Send Approval") {
    notification.value = {
      show: true,
      type: response ? "success" : "warning",
      message: response ? `Automation run successfully!` : `Automation failed!`,
    };
  }

  return response;
}

async function sendEmail(node) {
  const url = envVariables.value.REACT_APP_AZ_LA_EMAIL;
  const emailProperties = node.extras.properties.Scope;

  return await axios.post(url, emailProperties);
}

async function sendApproval(node, entryData) {
  const url = envVariables.value.REACT_APP_AZ_LA_APPROVAL;
  const approvalProperties = { ...node.extras.properties.Scope, entryData: entryData };

  return await axios.post(url, approvalProperties);
}

function openHyperlink(node) {
  const url = node.extras.properties.Scope.Hyperlink;

  try {
    new URL(url);
    return window.open(url, "_blank").focus();
  } catch (error) {
    console.error(error);
    notification.value = {
      show: true,
      type: "warning",
      message: "Failed to open hyperlink: Invalid URL!",
    };
    return null;
  }
}

async function submitTemplate(manualEvent, handleSubmit) {
  return await handleSubmit(manualEvent);
}

function stripHtmlTags(html) {
  return html.replace(/<[^>]*>/g, "");
}

function toBooleanResult(value) {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") {
      return true;
    }
  }

  if (typeof value === "number") {
    return value !== 0;
  }

  return false;
}

function evaluateExpressionTemplate(templateText, context = {}) {
  const fieldPattern = /\{\{(.+?)\}\}/g;
  const mathPattern = /=\[\[(.+?)\]\]/g;

  const toMathLiteral = (value) => {
    if (value === null || value === undefined) {
      return '""';
    }
    if (typeof value === "string") {
      return JSON.stringify(value);
    }
    if (typeof value === "object") {
      return JSON.stringify(value);
    }
    return String(value);
  };

  let resolvedText = String(templateText ?? "");
  resolvedText = resolvedText.replace(mathPattern, (match, expression) => {
    try {
      const expressionWithValues = expression.replace(fieldPattern, (fieldMatch, fieldNameRaw) => {
        const fieldName = fieldNameRaw.trim();
        if (!Object.hasOwn(context, fieldName)) {
          console.warn(`Field "${fieldName}" not found in expression context`);
          return fieldMatch;
        }

        return toMathLiteral(context[fieldName]);
      });
      const result = math.evaluate(expressionWithValues);
      return result.toString();
    } catch (error) {
      console.error(`Error evaluating expression: ${match}`, error);
      notification.value = {
        show: true,
        type: "warning",
        message: `Error evaluating expression: ${match}`,
      };
      return null;
    }
  });

  resolvedText = resolvedText.replace(fieldPattern, (match, fieldNameRaw) => {
    const fieldName = fieldNameRaw.trim();
    if (!Object.hasOwn(context, fieldName)) {
      console.warn(`Field "${fieldName}" not found in expression context`);
      return match;
    }

    const value = context[fieldName];
    if (value === null || value === undefined) {
      return "";
    }
    return typeof value === "object" ? JSON.stringify(value) : value;
  });

  return resolvedText;
}

function getEmailsFromPeoplePicker(scope) {
  const parser = math.parser();
  //all three other functions combined into one... parse if needed, the map to get the property, then join into a string
  if (typeof scope.arg0 !== "string") {
    console.error(`Error in getProperty function: Invalid arguments`);
    return null;
  }
  parser.set("parse", (str) => JSON.tryParse(str));
  //parse if arg0 is a string
  let parsed = scope.arg0;
  if (typeof scope.arg0 === "string") {
    parsed = parser.evaluate(`parse(${scope.arg0})`);
  }
  //map to get the property
  let mapped = parsed;
  if (Array.isArray(parsed)) {
    mapped = parsed.map((item) => {
      if (typeof item === "object" && item !== null) {
        return item["email"];
      } else {
        console.warn(`Cannot get property email of non-object item`);
        return undefined;
      }
    });
  }
  //join into a string
  return mapped.join(", ");
}

function getEmailsFromPeoplePickerInTable(scope, automationName) {
  let table = JSON.tryParse(scope.arg0);
  if (automationName === "Send Approval Notification DACS") {
    table = table.filter((row) => row["Pre Approval Status"] === "Pre-Approval Pending");
  }
  if (automationName === "Send Final Notification DACS") {
    table = table.filter((row) => row["Final Approval Status"] === "Final Approval Pending");
  }

  if (scope.arg2) {
    const filterExpression = scope.arg2;
    table = table.filter((row) => {
      try {
        const resolvedExpression = evaluateExpressionTemplate(filterExpression, row);
        return toBooleanResult(resolvedExpression);
      } catch (error) {
        console.error(`Error evaluating filter expression: "${filterExpression}"`, error);
        return false;
      }
    });
  }

  const column = table.map((row) => {
    const parsedRow = row[scope.arg1] ? JSON.tryParse(row[scope.arg1]) : [];
    return Array.isArray(parsedRow) ? parsedRow : [parsedRow];
  });
  const emails = column.flat().map((person) => person?.["email"]);
  return emails.join(", ");
}
